import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PAGE_DEFAULT, PAGE_SIZE_OPTIONS } from '../../constants';
import Icon from '../../icons';
import { CustomPaginationProps } from '../../interfaces';
import CustomSelect from '../Select';
import {
  AmiliArrowButton,
  AmiliPageWrapper,
  AmiliPagesWrapper,
  AmiliPaginationWrapper,
  CustomTotal,
  SizeChangerWrapper,
} from './styles';

type BaseParamsNavigate = {
  take: number;
  page: number;
};

type ExpandCustomPaginationProps = CustomPaginationProps & {
  pageSettingStates: {
    page: number;
    pageSize: number;
    setPage: (value: number) => void;
    setPageSize: (value: number) => void;
  };
  onNavigate?: <P extends BaseParamsNavigate>(params: P) => void;
};

const DuplicateAmiliPagination: React.FC<
  ExpandCustomPaginationProps
> = props => {
  const {
    total: customTotal,
    onChange: customChange,
    showSizeChanger = true,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
    subTotalValue, // for case that need render another "total" at the same time with total item
    pageSettingStates, //props from hook query
    onNavigate,
  } = props;
  const { page, pageSize, setPage, setPageSize } = pageSettingStates;

  const { t } = useTranslation();

  const [total, setTotal] = useState(0);
  useEffect(() => {
    if (customTotal) setTotal(customTotal);
  }, [customTotal]);

  const selectOptions = useMemo(() => {
    return pageSizeOptions.map(option => ({
      label: t('design.pageSize', { QUANTITY: option }),
      value: option,
    }));
  }, [pageSizeOptions, t]);

  /**
   * Update take, page params for query data when:
   * 1. Go to the previous/next page
   * 2. Click into the number of pagination
   * 3. Change the take page selection
   */
  const handleChange = useCallback(
    (page: number, pageSize: number) => {
      if (customChange) customChange(page, pageSize);
      if (onNavigate) onNavigate({ page, take: pageSize });

      //update 2 params in query key
      setPage(page);
      setPageSize(pageSize);
    },
    [customChange, onNavigate, setPage, setPageSize],
  );

  const pages = Math.ceil(total / pageSize);

  const rangePage = useMemo(() => {
    const max = Math.ceil(page / 10) * 10;
    const maxOfPages = Math.min(max, pages);

    return { from: max - 9, to: maxOfPages };
  }, [page, pages]);

  const handlePrev = () => {
    if (page > 10) {
      handleChange(rangePage.from - 10, pageSize);
    }
  };

  const handleNext = () => {
    const value = rangePage.to + 1;
    if (value <= pages) {
      handleChange(value, pageSize);
    }
  };

  return (
    <AmiliPaginationWrapper>
      <div>
        <CustomTotal>{t('design.total', { QUANTITY: total })}</CustomTotal>
        {(subTotalValue || subTotalValue === 0) && (
          <CustomTotal>
            {t('design.inventorySummary', { liveStock: subTotalValue })}
          </CustomTotal>
        )}
      </div>

      <AmiliPagesWrapper className="pages-wrapper">
        <AmiliArrowButton
          className={classNames({ disabled: rangePage.from === 1 })}
          onClick={handlePrev}
        >
          <Icon name="chevronLeft" width={9} height={12} color="var(--link)" />
        </AmiliArrowButton>

        {Array(Math.min(pages, 10))
          .fill(null)
          .map((_: any, index: number) => {
            const value = index + rangePage.from;

            if (value > rangePage.to) return <div key={index}></div>;
            return (
              <AmiliPageWrapper
                key={index}
                onClick={() => handleChange(value, pageSize)}
                className={classNames({ active: page === value })}
              >
                {value}
              </AmiliPageWrapper>
            );
          })}

        {total === 0 && <AmiliPageWrapper>0</AmiliPageWrapper>}

        <AmiliArrowButton
          className={classNames({ disabled: rangePage.to === pages })}
          onClick={handleNext}
        >
          <Icon name="chevronRight" width={9} height={12} color="var(--link)" />
        </AmiliArrowButton>
      </AmiliPagesWrapper>

      {showSizeChanger && (
        <SizeChangerWrapper>
          <CustomSelect
            type="linkage"
            value={pageSize}
            options={selectOptions}
            defaultValue={pageSize}
            popupClassName="pagination-popup"
            onChange={value => {
              //Go to the latest page if the current page is greater than the new page size
              // const newPage = Math.min(Math.ceil(total / Number(value)), page);

              //Go to the first page when the page size is changed
              handleChange(PAGE_DEFAULT, Number(value));
            }}
          />
        </SizeChangerWrapper>
      )}
    </AmiliPaginationWrapper>
  );
};

export default DuplicateAmiliPagination;
